import React, { useEffect, useMemo, useState } from 'react';
import { Divider, Flex } from 'antd';
import { faSlack } from '@fortawesome/free-brands-svg-icons';
import { faSearch } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { debounce, sortBy } from 'lodash-es';
import { css } from 'styled-components';

import { trackEvent } from 'jf/analytics/Analytics';
import { SlackTargetingClient, SlackUser } from 'jf/api';
import { useDevExTheme } from 'jf/common/themes/DevExTheme';
import { DevExButton } from 'jf/components/DevExButton';
import { DevExDrawer } from 'jf/components/DevExDrawer';
import { DevExEmpty } from 'jf/components/DevExEmpty';
import { DevExLoader } from 'jf/components/DevExLoader';
import { DevExSelect } from 'jf/components/DevExSelect';
import { useClientMutation, useClientQuery } from 'jf/utils/useClientQuery';

import { SlackEntityDisplay } from './SlackEntityDisplay';
import { SlackTargetList } from './SlackTargetList';

type SlackTargetDrawerProps = {
  open: boolean;
  onClose: () => void;
  slackUsers: SlackUser[];
  onDuplicateChange: (dupes: number) => void;
  addUsers: (users: SlackUser[]) => void;
};

const styles = {
  select: css`
    &&&&.ant-select {
      min-width: 520px;
      &.ant-select-lg {
        .ant-select-selector {
          border-radius: 5px;
        }
      }
    }
  `,
};

export const SlackTargetDrawer: React.FC<SlackTargetDrawerProps> = (props) => {
  const theme = useDevExTheme();

  const [searchQuery, setSearchQuery] = useState('');

  const [slackUsers, setSlackUsers] = useState<SlackUser[]>([]);

  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  const { data: slackEntities, isFetching } = useClientQuery(
    SlackTargetingClient.getSlackEntities,
    { filter: debouncedSearchQuery },
    { enabled: debouncedSearchQuery.length >= 1 }
  );

  const { mutateAsync: expandSlackEntity, isLoading: isExpanding } = useClientMutation(
    SlackTargetingClient.expandSlackEntity
  );

  const debounceFiredSearchQuery = useMemo(() => debounce(setDebouncedSearchQuery, 300), []);
  useEffect(() => {
    if (searchQuery) {
      debounceFiredSearchQuery(searchQuery);
    } else {
      debounceFiredSearchQuery.cancel();
      setDebouncedSearchQuery('');
    }
  }, [searchQuery]);

  const isSearching = searchQuery !== debouncedSearchQuery || isFetching;

  const slackEntityOptions = useMemo(() => {
    return sortBy(slackEntities, (entity) => entity.name.toLowerCase()).map((entity) => ({
      value: entity.id,
      label: (
        <SlackEntityDisplay
          slackUser={{
            publicReferenceId: entity.id,
            email: entity.email || '',
            slackUserId: entity.id,
            realName: entity.displayName || '',
            userName: entity.name,
            profileImageUrl: entity.image || '',
          }}
          entityType={entity.type}
          previewVersion
        />
      ),
    }));
  }, [slackEntities]);

  return (
    <DevExDrawer
      title="Add people from Slack"
      open={props.open}
      onClose={() => {
        setSlackUsers([]);
        props.onClose();
      }}
      width={640}
      closable
      extra={
        <Flex gap={theme.variable.spacing.sm}>
          <DevExButton
            onClick={() => {
              setSlackUsers([]);
              props.onClose();
            }}
          >
            Cancel
          </DevExButton>
          <DevExButton
            type="primary"
            onClick={() => {
              props.onDuplicateChange(
                slackUsers.filter((user) =>
                  props.slackUsers.some((target) => target.slackUserId === user.slackUserId)
                ).length
              );
              props.addUsers(
                slackUsers.filter(
                  (user) =>
                    !props.slackUsers.some((target) => target.slackUserId === user.slackUserId)
                )
              );
              setSlackUsers([]);
              props.onClose();
            }}
            disabled={slackUsers?.length === 0 ? true : false}
          >
            Add
          </DevExButton>
        </Flex>
      }
    >
      <Flex vertical gap={theme.variable.spacing.md}>
        <div>Add users by searching in Slack</div>
        <Flex justify="space-between" align="center">
          <DevExSelect
            css={styles.select}
            showSearch
            filterOption={() => true} // leave filtering to the BE only
            options={!isSearching ? slackEntityOptions : undefined}
            value={[]}
            onChange={(value) => {
              const entity = slackEntities?.find((entity) => entity.id === value);
              if (entity) {
                expandSlackEntity({
                  requestBody: {
                    entityId: entity.id,
                    entityType: entity.type,
                  },
                }).then((newSlackUsers) => {
                  setSlackUsers((users) => {
                    return [
                      ...users,
                      // filter out duplicates before adding
                      ...newSlackUsers.filter(
                        (newTarget) =>
                          !users.some((target) => target.slackUserId === newTarget.slackUserId)
                      ),
                    ];
                  });
                  trackEvent('survey-editor:slack-target-drawer:expand-slack-entity', {
                    slackUserId: entity.id,
                    entityType: entity.type,
                  });
                });
              }
              setSearchQuery('');
            }}
            placeholder="Search for @users, @groups, and #channels"
            loading={isSearching}
            suffixIcon={<FontAwesomeIcon icon={faSearch} />}
            notFoundContent={
              isSearching ? (
                <DevExLoader tip="Searching Slack..." />
              ) : (
                <DevExEmpty
                  icon={faSlack}
                  label={
                    searchQuery
                      ? `No slack targets found matching "${searchQuery}".`
                      : 'Search for @users, @groups, and #channels in your Slack instance.'
                  }
                />
              )
            }
            onSearch={setSearchQuery}
          />
        </Flex>
        <Divider style={{ margin: `${theme.variable.spacing.sm} 0` }} />
        <SlackTargetList
          users={slackUsers}
          onUpdate={setSlackUsers}
          emptyText="Start adding people from Slack by searching for them."
          trackEventKey="slack-target-drawer"
          loading={isExpanding}
          loadingText="Adding people from Slack..."
        />
      </Flex>
    </DevExDrawer>
  );
};
